import { useEffect, useState } from "react";
import { fetchDashboardData } from "../api/dashboardApi";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData()
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-sm text-gray-500">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-5 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
          Admin Dashboard
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Overview of your store
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
        <StatCard
          title="Total Products"
          value={data.totalProducts}
        />

        <StatCard
          title="Total Orders"
          value={data.totalOrders}
        />

        <StatCard
          title="Revenue"
          value={`₹${Number(data.totalRevenue || 0).toLocaleString("en-IN")}`}
        />
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Section Header */}
        <div className="px-4 py-4 sm:px-6 border-b border-gray-100">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800">
            Recent Orders
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Latest customer orders
          </p>
        </div>

        {/* ---------------- DESKTOP TABLE ---------------- */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Order ID
                </th>

                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Customer
                </th>

                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Amount
                </th>

                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Status
                </th>
              </tr>
            </thead>

            <tbody>
              {data.recentOrders?.length > 0 ? (
                data.recentOrders.map((order) => (
                  <tr
                    key={order._id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition"
                  >
                    <td className="px-6 py-4 text-sm text-gray-700 font-medium">
                      #{order._id.slice(-6)}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700">
                      {order.user?.name || "Guest"}
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      ₹{Number(order.totalPrice || 0).toLocaleString("en-IN")}
                    </td>

                    <td className="px-6 py-4">
                      <StatusBadge status={order.status} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-sm text-gray-500"
                  >
                    No recent orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ---------------- MOBILE CARDS ---------------- */}
        <div className="block sm:hidden">
          {data.recentOrders?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {data.recentOrders.map((order) => (
                <div
                  key={order._id}
                  className="p-4"
                >
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-1">
                        Order ID
                      </p>

                      <p className="text-sm font-semibold text-gray-800">
                        #{order._id.slice(-6)}
                      </p>
                    </div>

                    <StatusBadge status={order.status} />
                  </div>

                  {/* Customer */}
                  <div className="mt-4">
                    <p className="text-xs text-gray-500">
                      Customer
                    </p>

                    <p className="text-sm font-medium text-gray-800 mt-1 truncate">
                      {order.user?.name || "Guest"}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">
                      Amount
                    </p>

                    <p className="text-base font-bold text-gray-900 mt-1">
                      ₹
                      {Number(order.totalPrice || 0).toLocaleString(
                        "en-IN"
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              No recent orders found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Stat Card ---------- */

function StatCard({ title, value }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
      <p className="text-gray-500 text-xs sm:text-sm">
        {title}
      </p>

      <p className="text-xl sm:text-2xl font-bold text-gray-800 mt-1 sm:mt-2 break-words">
        {value}
      </p>
    </div>
  );
}

/* ---------- Status Badge ---------- */

function StatusBadge({ status }) {
  let classes = "bg-gray-100 text-gray-600";

  if (status === "Completed") {
    classes = "bg-green-100 text-green-700";
  } else if (status === "Pending") {
    classes = "bg-yellow-100 text-yellow-700";
  } else if (status === "Cancelled" || status === "Failed") {
    classes = "bg-red-100 text-red-700";
  }

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${classes}`}
    >
      {status}
    </span>
  );
}